/**
 * TypeScript mirror of SequencerState from WASM memory
 * Reads state directly from WASM module memory using Struct definitions
 */

import { MAX_NOTES, readStaticInt32Array, SequenceDataStruct, SequencerStateStruct } from './sequencer-struct.ts'

export interface SequencerStateMirror {
  // From SequenceData (via data pointer)
  sequenceId: number
  swing: number
  offset: number
  exponent: number

  // From SequencerState
  maxPitches: number
  totalSamples: number
  currentIndex: number
  prevIndex: number
  lastTrig: number
  lastResetTrig: number
  arrayOffset: number
  slideTime: number
  slideDuration: number
  trigHoldSamplesRemaining: number
  pendingTrigHold: number
  lastNoteIndex: number
  shuffleTriggerCount: number
  shuffleCurrentIndex: number
  lastShuffleTrig: number
  lastShuffleSampleCounter: number
  lastShuffleTriggerSample: number
  rotationTriggerCount: number
  lastRotationTrig: number
  lastRotationSampleCounter: number
  lastRotationTriggerSample: number
  patternStartSample: number

  // Arrays (read from pointers)
  noteStarts: Int32Array | null
  shuffledIndices: Int32Array | null
  rotatedIndices: Int32Array | null
}

/**
 * Read SequencerState from WASM memory
 * @param wasmMemory WASM module memory (WebAssembly.Memory)
 * @param statePtr Pointer to SequencerState instance
 * @returns SequencerStateMirror or null if pointer is invalid
 */
export function readSequencerState(
  wasmMemory: WebAssembly.Memory,
  statePtr: number,
): SequencerStateMirror | null {
  if (statePtr === 0) return null

  const buffer = wasmMemory.buffer

  try {
    // Read SequencerState struct
    const stateView = SequencerStateStruct(buffer, statePtr)

    // Read SequenceData (via data pointer)
    const dataPtr = stateView.data as unknown as number
    if (dataPtr === 0) return null

    const dataView = SequenceDataStruct(buffer, dataPtr)

    // Read arrays from pointers
    const noteStartsPtr = stateView.noteStarts as unknown as number
    const noteStarts = readStaticInt32Array(buffer, noteStartsPtr, MAX_NOTES)

    const shuffledIndicesPtr = stateView.shuffledIndices as unknown as number
    const shuffledIndices = readStaticInt32Array(buffer, shuffledIndicesPtr, MAX_NOTES)

    const rotatedIndicesPtr = stateView.rotatedIndices as unknown as number
    const rotatedIndices = readStaticInt32Array(buffer, rotatedIndicesPtr, MAX_NOTES)

    return {
      sequenceId: dataView.id as number,
      swing: dataView.swing as number,
      offset: dataView.offset as number,
      exponent: dataView.exponent as number,
      maxPitches: stateView.maxPitches as number,
      totalSamples: stateView.totalSamples as number,
      currentIndex: stateView.currentIndex as number,
      prevIndex: stateView.prevIndex as number,
      lastTrig: stateView.lastTrig as number,
      lastResetTrig: stateView.lastResetTrig as number,
      arrayOffset: stateView.arrayOffset as number,
      slideTime: stateView.slideTime as number,
      slideDuration: stateView.slideDuration as number,
      trigHoldSamplesRemaining: stateView.trigHoldSamplesRemaining as number,
      pendingTrigHold: stateView.pendingTrigHold as number,
      lastNoteIndex: stateView.lastNoteIndex as number,
      shuffleTriggerCount: stateView.shuffleTriggerCount as number,
      shuffleCurrentIndex: stateView.shuffleCurrentIndex as number,
      lastShuffleTrig: stateView.lastShuffleTrig as number,
      lastShuffleSampleCounter: stateView.lastShuffleSampleCounter as number,
      lastShuffleTriggerSample: stateView.lastShuffleTriggerSample as number,
      rotationTriggerCount: stateView.rotationTriggerCount as number,
      lastRotationTrig: stateView.lastRotationTrig as number,
      lastRotationSampleCounter: stateView.lastRotationSampleCounter as number,
      lastRotationTriggerSample: stateView.lastRotationTriggerSample as number,
      patternStartSample: stateView.patternStartSample as number,
      noteStarts,
      shuffledIndices,
      rotatedIndices,
    }
  }
  catch (error) {
    console.error('Error reading SequencerState:', error)
    return null
  }
}

/**
 * Get all sequencer state mirrors from WASM shared memory
 * @param wasmMemory WASM shared memory buffer (SharedArrayBuffer)
 * @param getSequencerStatePtr Function to get sequencer state pointer by ID
 * @param getSequencerSequenceId Function to get sequence ID from sequencer ID
 * @returns Map of sequenceId -> SequencerStateMirror
 */
export function getAllSequencerStates(
  wasmMemory: SharedArrayBuffer,
  getSequencerStatePtr: (id: number) => number,
  getSequencerSequenceId: (id: number) => number,
): Map<number, SequencerStateMirror> {
  const states = new Map<number, SequencerStateMirror>()

  // Iterate through possible sequencer IDs (0 to 1000)
  for (let seqId = 0; seqId < 1000; seqId++) {
    const statePtr = getSequencerStatePtr(seqId)
    if (statePtr === 0) continue

    const state = readSequencerStateFromBuffer(wasmMemory, statePtr)
    if (state) {
      const sequenceId = getSequencerSequenceId(seqId)
      if (sequenceId >= 0) {
        states.set(sequenceId, state)
      }
    }
  }

  return states
}

/**
 * Read SequencerState from shared memory buffer (for use from main thread)
 * @param buffer SharedArrayBuffer from WASM memory
 * @param statePtr Pointer to SequencerState instance
 * @returns SequencerStateMirror or null if pointer is invalid
 */
export function readSequencerStateFromBuffer(
  buffer: SharedArrayBuffer,
  statePtr: number,
): SequencerStateMirror | null {
  if (statePtr === 0) return null

  try {
    // Read SequencerState struct (cast SharedArrayBuffer to ArrayBuffer for Struct)
    const bufferAsArrayBuffer = buffer as unknown as ArrayBuffer
    const stateView = SequencerStateStruct(bufferAsArrayBuffer, statePtr)

    // Read SequenceData (via data pointer)
    const dataPtr = stateView.data as unknown as number
    if (dataPtr === 0) return null

    const dataView = SequenceDataStruct(bufferAsArrayBuffer, dataPtr)

    // Read arrays from pointers
    const noteStartsPtr = stateView.noteStarts as unknown as number
    const noteStarts = readStaticInt32Array(buffer, noteStartsPtr, MAX_NOTES)

    const shuffledIndicesPtr = stateView.shuffledIndices as unknown as number
    const shuffledIndices = readStaticInt32Array(buffer, shuffledIndicesPtr, MAX_NOTES)

    const rotatedIndicesPtr = stateView.rotatedIndices as unknown as number
    const rotatedIndices = readStaticInt32Array(buffer, rotatedIndicesPtr, MAX_NOTES)

    return {
      sequenceId: dataView.id as number,
      swing: dataView.swing as number,
      offset: dataView.offset as number,
      exponent: dataView.exponent as number,
      maxPitches: stateView.maxPitches as number,
      totalSamples: stateView.totalSamples as number,
      currentIndex: stateView.currentIndex as number,
      prevIndex: stateView.prevIndex as number,
      lastTrig: stateView.lastTrig as number,
      lastResetTrig: stateView.lastResetTrig as number,
      arrayOffset: stateView.arrayOffset as number,
      slideTime: stateView.slideTime as number,
      slideDuration: stateView.slideDuration as number,
      trigHoldSamplesRemaining: stateView.trigHoldSamplesRemaining as number,
      pendingTrigHold: stateView.pendingTrigHold as number,
      lastNoteIndex: stateView.lastNoteIndex as number,
      shuffleTriggerCount: stateView.shuffleTriggerCount as number,
      shuffleCurrentIndex: stateView.shuffleCurrentIndex as number,
      lastShuffleTrig: stateView.lastShuffleTrig as number,
      lastShuffleSampleCounter: stateView.lastShuffleSampleCounter as number,
      lastShuffleTriggerSample: stateView.lastShuffleTriggerSample as number,
      rotationTriggerCount: stateView.rotationTriggerCount as number,
      lastRotationTrig: stateView.lastRotationTrig as number,
      lastRotationSampleCounter: stateView.lastRotationSampleCounter as number,
      lastRotationTriggerSample: stateView.lastRotationTriggerSample as number,
      patternStartSample: stateView.patternStartSample as number,
      noteStarts,
      shuffledIndices,
      rotatedIndices,
    }
  }
  catch (error) {
    console.error('Error reading SequencerState:', error)
    return null
  }
}
